import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import L from "leaflet";
import type { MarkerClusterGroup } from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import "leaflet.heat";
import api from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import LoadingSplash from "@/components/LoadingSplash";
import "./ReportsCoverage.css";

type MapExtent = {
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
};

type MapPoint = {
  address_id: number;
  latitude: number;
  longitude: number;
  status: string;
  user_id: number;
  timestamp: string;
};

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

async function fetchMapExtent(): Promise<MapExtent> {
  const res = await api.get("/map/extent/");
  return res.data;
}

async function fetchMapPoints(bbox: string): Promise<MapPoint[]> {
  const res = await api.get("/map/points/", { params: { bbox } });
  return res.data;
}

const isGreenStatus = (status: string) => status === "REGULAR" || status === "CUSTOMER";

const createMarkerIcon = (status: string) =>
  L.divIcon({
    className: `coverage-marker leaflet-div-icon ${
      isGreenStatus(status) ? "coverage-marker--green" : "coverage-marker--red"
    }`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -8],
  });

export default function ReportsCoverageHeatmap() {
  const { user, loading: authLoading } = useAuth();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const clusterRef = useRef<MarkerClusterGroup | null>(null);
  const heatLayerRef = useRef<L.HeatLayer | null>(null);
  const [bbox, setBbox] = useState<string>("");

  const extentQuery = useQuery({
    queryKey: ["coverage-extent"],
    queryFn: fetchMapExtent,
    staleTime: 5 * 60_000,
  });

  const debouncedUpdateBounds = useMemo(
    () =>
      debounce(() => {
        const map = mapRef.current;
        if (!map) return;
        const bounds = map.getBounds();
        const west = bounds.getWest();
        const south = bounds.getSouth();
        const east = bounds.getEast();
        const north = bounds.getNorth();
        setBbox(`${west},${south},${east},${north}`);
      }, 300),
    []
  );

  useEffect(() => {
    return () => debouncedUpdateBounds.cancel();
  }, [debouncedUpdateBounds]);

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = L.map(mapContainerRef.current, { preferCanvas: true });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map);

    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: false,
      maxClusterRadius: 30  ,
    disableClusteringAtZoom: 16  
    });

    const heatLayer = L.heatLayer([], {
      radius: 22,
      blur: 18,
      maxZoom: 18,
      minOpacity: 0.35,
      gradient: {
        0.2: "#b3e5fc",
        0.4: "#64b5f6",
        0.6: "#1976d2",
        0.8: "#ef6c00",
        1.0: "#c62828",
      },
    });

    mapRef.current = map;
    clusterRef.current = cluster;
    heatLayerRef.current = heatLayer;

    map.addLayer(cluster);
    map.addLayer(heatLayer);
    map.on("moveend", debouncedUpdateBounds);

    map.on("zoom", () => {
      if (heatLayerRef.current) heatLayerRef.current.redraw();
    });
    map.on("move", () => {
      if (heatLayerRef.current) heatLayerRef.current.redraw();
    });

    // Example throttled redraw:
    // let heatRedrawTimeout: number | null = null;
    // map.on("zoom", () => {
    //   if (!heatLayerRef.current) return;
    //   if (!heatRedrawTimeout) {
    //     heatRedrawTimeout = window.setTimeout(() => {
    //       heatLayerRef.current?.redraw();
    //       heatRedrawTimeout = null;
    //     }, 80);
    //   }
    // });
  }, [debouncedUpdateBounds]);

  useEffect(() => {
    const map = mapRef.current;
    const bounds = extentQuery.data;
    if (!map || !bounds) return;

    const fitBounds = L.latLngBounds(
      [bounds.min_lat, bounds.min_lng],
      [bounds.max_lat, bounds.max_lng]
    );
    map.fitBounds(fitBounds, { padding: [24, 24] });
    debouncedUpdateBounds();
  }, [extentQuery.data, debouncedUpdateBounds]);

  const pointsQuery = useQuery<MapPoint[]>({
    queryKey: ["coverage-points", bbox],
    queryFn: () => fetchMapPoints(bbox),
    enabled: !!bbox,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    const cluster = clusterRef.current;
    const heatLayer = heatLayerRef.current;
    if (!cluster || !heatLayer) return;

    const points: MapPoint[] = pointsQuery.data ?? [];

    cluster.clearLayers();
    heatLayer.setLatLngs([]);

    const heatPoints: [number, number, number][] = [];

    points.forEach((point) => {
      const marker = L.marker([point.latitude, point.longitude], {
        icon: createMarkerIcon(point.status),
      });
      marker.bindPopup(
        `<div class="coverage-popup">
          <div><strong>Status</strong> ${point.status}</div>
          <div><strong>Timestamp</strong> ${new Date(point.timestamp).toLocaleString()}</div>
          <div><strong>User ID</strong> ${point.user_id}</div>
        </div>`
      );
      cluster.addLayer(marker);
      heatPoints.push([point.latitude, point.longitude, 0.8]);
    });

    heatLayer.setLatLngs(heatPoints);
  }, [pointsQuery.data]);

  useEffect(() => {
    return () => {
      mapRef.current?.off();
      mapRef.current?.remove();
      mapRef.current = null;
      clusterRef.current = null;
      heatLayerRef.current = null;
    };
  }, []);

  if (authLoading) return <LoadingSplash />;
  if (!user?.is_superuser) return <Navigate to="/search" replace />;

  const showMapSpinner = extentQuery.isPending || (pointsQuery.isPending && !pointsQuery.data);

  return (
    <Box sx={{ px: 2, pb: 3 }}>
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Heatmap overlays intensity on top of the clustered markers.
          </Typography>
          <Box className="coverage-map-container">
            <div ref={mapContainerRef} className="coverage-map" />
            {showMapSpinner && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.65)",
                  zIndex: 500,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
